import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  ShieldCheck,
  Sparkles,
  LineChart,
  Users,
  User,
  Eye,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export function Landing() {
  const { t } = useTranslation(['common', 'billing']);
  return (
    <div className="mx-auto max-w-6xl px-4">
      {/* Hero */}
      <section className="flex flex-col items-center gap-6 py-16 text-center md:py-24">
        <span className="rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          {t('landing.hero_kicker')}
        </span>
        <h1 className="max-w-3xl text-balance text-4xl font-bold tracking-tight md:text-6xl">
          {t('tagline')}
        </h1>
        <p className="max-w-2xl text-balance text-muted-foreground md:text-lg">
          {t('landing.hero_subhead')}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button asChild size="lg">
            <Link to="/showcase">
              <Eye className="mr-2 h-4 w-4" />
              {t('landing.cta_explore')}
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <Link to="/login">{t('landing.cta_login')}</Link>
          </Button>
        </div>
        <p className="max-w-md text-xs text-muted-foreground">{t('marketing_disclaimer')}</p>
      </section>

      {/* Three features */}
      <section className="grid gap-6 py-8 md:grid-cols-3">
        <Feature
          icon={Sparkles}
          title={t('landing.feature_plans_title')}
          body={t('landing.feature_plans_body')}
        />
        <Feature
          icon={ShieldCheck}
          title={t('landing.feature_eu_title')}
          body={t('landing.feature_eu_body')}
        />
        <Feature
          icon={LineChart}
          title={t('landing.feature_progress_title')}
          body={t('landing.feature_progress_body')}
        />
      </section>

      {/* For whom */}
      <section className="py-16">
        <h2 className="mb-8 text-center text-3xl font-bold tracking-tight">
          {t('landing.for_whom_title')}
        </h2>
        <div className="grid gap-6 md:grid-cols-2">
          <Audience
            icon={Users}
            title={t('landing.for_whom_trainer_title')}
            body={t('landing.for_whom_trainer_body')}
          />
          <Audience
            icon={User}
            title={t('landing.for_whom_client_title')}
            body={t('landing.for_whom_client_body')}
          />
        </div>
      </section>

      {/* See it in action — primary conversion CTA */}
      <section className="my-12 rounded-2xl border border-primary/30 bg-primary/5 p-8 text-center md:p-12">
        <h2 className="text-balance text-3xl font-bold tracking-tight md:text-4xl">
          {t('landing.see_it_title')}
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-balance text-muted-foreground">
          {t('landing.see_it_body')}
        </p>
        <Button asChild size="lg" className="mt-6">
          <Link to="/showcase">
            <Eye className="mr-2 h-4 w-4" />
            {t('landing.cta_explore')}
            <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </Button>
      </section>
    </div>
  );
}

function Feature({ icon: Icon, title, body }: { icon: LucideIcon; title: string; body: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <Icon className="h-6 w-6 text-primary" />
      <h3 className="mt-4 font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

function Audience({ icon: Icon, title, body }: { icon: LucideIcon; title: string; body: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-lg bg-primary/15 text-primary">
          <Icon className="h-5 w-5" />
        </span>
        <h3 className="text-lg font-semibold">{title}</h3>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}
